import { TaxStrategy } from '@principle-theorem/accounting';
import {
  EventType,
  InvoiceLineItemType,
  ParticipantType,
  RefundType,
} from '@principle-theorem/principle-core/interfaces';
import {
  IAvroSerialisedRawInlineNodes,
  IAvroSerialisedVersionedSchema,
  ISerialisedCollectionReference,
  ISerialisedDocumentReference,
  ISerialisedTimestamp,
} from '@principle-theorem/shared';

export interface IKeyValuePair {
  key: string;
  value: string;
}

export interface IBigQueryFirestoreModel {
  createdAt: ISerialisedTimestamp;
  updatedAt?: ISerialisedTimestamp;
  deleted?: boolean;
}

export interface ITimestampedDimension {
  timestamp: ISerialisedTimestamp;
}

export interface IBigQueryReffable {
  ref: ISerialisedDocumentReference;
}

// TODO: Remove? May not need this.
export interface IBigQueryCollectionReffable {
  ref: ISerialisedCollectionReference;
}

export interface IBigQueryNamedDocument extends IBigQueryReffable {
  name: string;
}

export interface IBigQueryGeoCoordinates {
  latitude: number;
  longitude: number;
}

export interface IBigQueryStatusHistory<Status> {
  status: Status;
  updatedAt: ISerialisedTimestamp;
}

export interface IBigQueryEvent {
  from: ISerialisedTimestamp;
  to: ISerialisedTimestamp;
  practice?: IBigQueryNamedDocument;
  creator?: IBigQueryNamedDocument;
  organiser?: IBigQueryNamedDocument;
  participants: IBigQueryParticipant[];
  type: EventType;
}

interface IBigQueryParticipant extends IBigQueryNamedDocument {
  type: ParticipantType;
}

export interface IBigQueryNote {
  uid: string;
  content: IAvroSerialisedVersionedSchema | string;
  pinned: boolean;
  createdAt: ISerialisedTimestamp;
  updatedAt: ISerialisedTimestamp;
  deleted: boolean;
}

export interface IBigQueryInteraction {
  uid: string;
  /**
   * InteractionType is an invalid avro enum
   */
  type: string;
  owner?: IBigQueryNamedDocument;
  title: IAvroSerialisedRawInlineNodes;
  action?: string;
  createdAt: ISerialisedTimestamp;
  content: IAvroSerialisedVersionedSchema;
}

export interface IInvoiceDimensionItems {
  [InvoiceLineItemType.Product]: IBigQueryCustomLineItem[];
  [InvoiceLineItemType.Treatment]: IBigQueryTreatmentLineItem[];
  [InvoiceLineItemType.TreatmentBasePrice]: ITreatmentBasePriceLineItem[];
  [InvoiceLineItemType.ServiceCode]: IBigQueryServiceCodeLineItem[];
  [InvoiceLineItemType.Deposit]: IBigQueryDepositLineItem[];
  [InvoiceLineItemType.Refund]: IBigQueryRefundLineItem[];
  [InvoiceLineItemType.Fee]: IBigQueryCustomLineItem[];
}

export interface IBigQueryInvoiceLineItem {
  uid: string;
  description: string;
  amount: number;
  taxStatus: TaxStrategy;
  tax: number;
  quantity: number;
}

interface IBigQueryRefundLineItem extends IBigQueryCustomLineItem {
  refundType: RefundType;
  sourceInvoice: ISerialisedDocumentReference;
}

export interface IBigQueryTreatmentLineItem extends IBigQueryCustomLineItem {
  items: Partial<
    Pick<
      IInvoiceDimensionItems,
      InvoiceLineItemType.ServiceCode | InvoiceLineItemType.TreatmentBasePrice
    >
  >;
  treatmentRef: ITreatmentRef;
}

export interface IBigQueryDepositLineItem extends IBigQueryCustomLineItem {
  max: number;
  treatments: IBigQueryNamedDocument[];
}

export interface IBigQueryCustomLineItem extends IBigQueryInvoiceLineItem {
  type: InvoiceLineItemType;
}

export interface IBigQueryServiceCodeLineItem extends IBigQueryCustomLineItem {
  code: string;
  toothId?: string;
}

export interface ITreatmentRef {
  planRef?: ISerialisedDocumentReference;
  treatmentUuid: string;
  attributedTo: IBigQueryNamedDocument;
}

export interface ITreatmentBasePriceLineItem extends IBigQueryCustomLineItem {
  treatmentRef: ITreatmentRef;
}
